import React, { useState, useEffect, useCallback } from "react";
import AddNewAreaModal from "../../components/modal/AddNewAreaModal";
import AssignAreasToStoreModal from "../../components/modal/AssignAreasToStoreModal";
import AssignStoresToAreaModal from "../../components/modal/AssignStoreToAreasModal";
import {
    fetchDeliveryAreas,
    createDeliveryArea,
    updateDeliveryArea,
    deleteDeliveryArea,
    fetchDeliveryArea,
} from "../../service/api/orders"; // adjust path to wherever api.js lives
import "../../css/DeliveryAreaProSettings.css";

function timeAgo(dateStr) {
    if (!dateStr) return "";
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days < 1) return "today";
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
    const years = Math.floor(months / 12);
    return `${years} year${years > 1 ? "s" : ""} ago`;
}

function IconPlus() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
        </svg>
    );
}
function IconMap() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
            <line x1="8" y1="2" x2="8" y2="18" />
            <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
    );
}
function IconEdit() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
    );
}
function IconExternal() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
    );
}
function IconChevronDown() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}
function IconPlay() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
            <polygon points="10 8 16 12 10 16" />
        </svg>
    );
}

export default function DeliveryAreaProSettings() {
    const [areas, setAreas] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const [modalMode, setModalMode] = useState(null); // null | "add" | "edit"
    const [editingArea, setEditingArea] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [storesModalArea, setStoresModalArea] = useState(null); // the area whose "Assign to Stores" was clicked

    const loadAreas = useCallback(async (pageNum = 1) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchDeliveryAreas({ page: pageNum, per_page: 30 });
            setAreas(res.data || []);
            setHasMore(!!res.next_page_url);
            setPage(res.current_page || pageNum);
        } catch (e) {
            setError("Couldn't load delivery areas.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAreas(1);
    }, [loadAreas]);

    // Auto-dismiss the success banner after a few seconds.
    useEffect(() => {
        if (!successMsg) return;
        const t = setTimeout(() => setSuccessMsg(null), 4000);
        return () => clearTimeout(t);
    }, [successMsg]);

    const openAddModal = () => {
        setEditingArea(null);
        setModalMode("add");
    };

    const openEditModal = async (area) => {
        setModalMode("edit");
        setModalLoading(true);
        try {
            const res = await fetchDeliveryArea(area.id);
            setEditingArea(res.data);
        } catch (e) {
            setError("Couldn't load that area.");
            setModalMode(null);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setModalMode(null);
        setEditingArea(null);
    };

    const handleSaveArea = async (areaData) => {
        try {
            if (modalMode === "edit" && editingArea?.id) {
                await updateDeliveryArea(editingArea.id, areaData);
            } else {
                await createDeliveryArea(areaData);
            }
            await loadAreas(page);
        } catch (e) {
            setError("Couldn't save the area — check the fields and try again.");
        }
    };

    const handleDeleteArea = async () => {
        if (!editingArea?.id) return;
        try {
            await deleteDeliveryArea(editingArea.id);
            await loadAreas(page);
        } catch (e) {
            setError("Couldn't delete the area.");
        }
    };

    const handleAssignSaved = (store, areaIds) => {
        setSuccessMsg(`Updated delivery areas for ${store.name} (${areaIds.length} assigned).`);
    };

    const handleStoresAssignSaved = (area, storeIds) => {
        setSuccessMsg(`Updated stores served by ${area.name} (${storeIds.length} assigned).`);
    };

    return (
        <div className="dap-page">
            <div className="dap-header">
                <div className="dap-breadcrumb">
                    <span className="dap-crumb-active">Delivery Area Pro</span>
                </div>
                <div className="dap-actions">
                    <button className="dap-btn dap-btn-outline" onClick={openAddModal}>
                        <span className="dap-btn-icon-box"><IconPlus /></span>
                        <span>Add New Area</span>
                    </button>
                    <button className="dap-btn dap-btn-outline" onClick={() => setAssignModalOpen(true)}>
                        <span className="dap-btn-icon-box"><IconMap /></span>
                        <span>Assign Areas to Store</span>
                    </button>
                </div>
            </div>

            {error && <div className="dap-error">{error}</div>}
            {successMsg && <div className="dap-success">{successMsg}</div>}

            <div className="dap-card">
                <table className="dap-table">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Updated</th>
                        <th className="dap-th-sort"><IconChevronDown /></th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading && (
                        <tr><td colSpan={6} className="dap-muted">Loading…</td></tr>
                    )}
                    {!loading && areas.length === 0 && (
                        <tr><td colSpan={6} className="dap-muted">No delivery areas yet.</td></tr>
                    )}
                    {!loading && areas.map((area) => (
                        <tr key={area.id}>
                            <td className="dap-name">{area.name}</td>
                            <td className="dap-desc">{area.description}</td>
                            <td><span className="dap-status">{area.status}</span></td>
                            <td className="dap-muted">{timeAgo(area.created_at)}</td>
                            <td className="dap-muted">{timeAgo(area.updated_at)}</td>
                            <td>
                                <div className="dap-row-actions">
                                    <button className="dap-action-btn" onClick={() => openEditModal(area)}>
                                        <span>EDIT</span>
                                        <IconEdit />
                                    </button>
                                    <button className="dap-action-btn" onClick={() => setStoresModalArea(area)}>
                                        <span>Assign to Stores</span>
                                        <IconExternal />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {hasMore && !loading && (
                <button className="dap-btn dap-btn-outline" onClick={() => loadAreas(page + 1)}>
                    Load more
                </button>
            )}

            {modalMode && !modalLoading && (
                <AddNewAreaModal
                    key={modalMode === "edit" ? `edit-${editingArea?.id}` : "add"}
                    open={true}
                    mode={modalMode}
                    initialArea={editingArea}
                    onClose={closeModal}
                    onSave={handleSaveArea}
                    onDelete={handleDeleteArea}
                />
            )}

            <AssignAreasToStoreModal
                open={assignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                onSaved={handleAssignSaved}
            />

            <AssignStoresToAreaModal
                open={!!storesModalArea}
                area={storesModalArea}
                onClose={() => setStoresModalArea(null)}
                onSaved={handleStoresAssignSaved}
            />
        </div>
    );
}